// Generic side panel (bottom sheet on phones) for details and short flows:
// position, transfer, video, payment, support… Same look as the news preview.
import i18n from '@/i18n';
import { Component, createContext, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useBodyClass } from '@/app/providers/bodyClass';
import { cx } from '@/lib/format';
import { closeAbove, closeTopLayer, useLayer } from '@/lib/overlay';

type SheetSpec = { kicker?: ReactNode; title: ReactNode; body: ReactNode; className?: string };
type Ctx = { openSheet: (s: SheetSpec) => void; closeSheet: () => void; isOpen: boolean };
const SheetCtx = createContext<Ctx>({ openSheet: () => {}, closeSheet: () => {}, isOpen: false });
export const useSheet = () => useContext(SheetCtx);

/** A sheet can be opened from any page (e.g. «Указать позицию» from Orca's answer):
 *  its data may still be loading or fail — that stays inside the sheet. */
class SheetBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() { return this.state.failed ? <p className="empty">{i18n.t('common:state.sheetFailed')}</p> : this.props.children; }
}

export function SheetProvider({ children }: { children: ReactNode }) {
  const [spec, setSpec] = useState<SheetSpec | null>(null);
  const [shown, setShown] = useState<SheetSpec | null>(null);
  const open = !!spec;
  const bodyRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  useBodyClass('gsh-open', open);
  const openSheet = useCallback((s: SheetSpec) => setSpec(s), []);
  const closeSheet = useCallback(() => setSpec(null), []);
  // keep the content while sliding out
  useEffect(() => { if (spec) { setShown(spec); bodyRef.current?.scrollTo(0, 0); } }, [spec]);
  useEffect(() => { setSpec(null); }, [pathname]);
  // one modal stack: Esc / the scrim close the top layer; focus returns to the opener
  const top = useLayer('sheet', open, closeSheet);
  const asideRef = useRef<HTMLElement>(null);
  useEffect(() => { if (spec) asideRef.current?.focus({ preventScroll: true }); }, [spec]);
  const value = useMemo(() => ({ openSheet, closeSheet, isOpen: open }), [openSheet, closeSheet, open]);
  return (
    <SheetCtx.Provider value={value}>
      {children}
      <div className="gsh-scrim" onClick={closeTopLayer} />
      <aside className={cx('gsh', shown?.className)} role="dialog" aria-modal={top} aria-label={typeof shown?.title === 'string' ? shown.title : i18n.t('nav:details')} inert={!open}
        tabIndex={-1} ref={asideRef} onPointerDownCapture={() => closeAbove('sheet')}>
        <header className="gsh-bar">
          <div className="gsh-h">{shown?.kicker && <span className="gsh-k">{shown.kicker}</span>}<b>{shown?.title}</b></div>
          <button className="dr-x" type="button" aria-label={i18n.t('common:a11y.close')} onClick={closeSheet}><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18" /></svg></button>
        </header>
        <div className="gsh-body" ref={bodyRef}><SheetBoundary key={String(shown?.title)}><Suspense fallback={<div className="gsh-wait" aria-busy="true" />}>{shown?.body}</Suspense></SheetBoundary></div>
      </aside>
    </SheetCtx.Provider>
  );
}
